import React from 'react';

import Spinner from '../../../../../../Spinner/Spinner';
import { Type } from './types';
import {
  IconWrapper,
  StyledBasicParagraph,
  StyledErrorIcon,
  StyledFilledSuccessIcon,
  StyledInfoColorIcon,
  StyledWarningColorIcon,
  Wrapper,
} from './Toast.styled';

interface ToastProps {
  /**
   * Message to be displayed by the tooltip.
   */
  text: string | React.ReactNode;
  /**
   * Type of Toast depending on the objective of the message to be displayed. It can be one of the following:
   * `Toast.Type.INFO`, `Toast.Type.WARNING`, `Toast.Type.ERROR`, `Toast.Type.SUCCESS`, `Toast.Type.LOADING`.
   */
  type: Type;
  /**
   * Indicates that the Toast will disappear.
   */
  dead?: boolean;
  /**
   * Background color of the Toast
   */
  backgroundColor?: string;
  /**
   * Text color of the Toast
   */
  textColor?: string;
  /**
   *  Function that is executed when the Toast disappears.
   */
  onHide?: () => void;
}

const getIcon = (type: Type) => {
  switch (type) {
    case Type.SUCCESS:
      return StyledFilledSuccessIcon;
    case Type.ERROR:
      return StyledErrorIcon;
    case Type.WARNING:
      return StyledWarningColorIcon;
    case Type.INFO:
      return StyledInfoColorIcon;
    case Type.LOADING:
      return Spinner;
    default:
      return undefined;
  }
};

const Toast = ({
  text,
  type,
  dead = false,
  backgroundColor,
  textColor,
  onHide,
}: ToastProps) => {
  const Icon = getIcon(type);

  const handleAnimationEnd = () => {
    if (!dead) {
      return;
    }

    onHide?.();
  };

  return (
    <Wrapper
      data-cy={`${type}Toast`}
      $dead={dead}
      $backgroundColor={backgroundColor}
      onAnimationEnd={handleAnimationEnd}
    >
      {Icon && (
        <IconWrapper>
          <Icon aria-hidden="true" />
        </IconWrapper>
      )}
      {typeof text === 'string' ? (
        <StyledBasicParagraph
          role="alert"
          $color={textColor}
          dangerouslySetInnerHTML={{ __html: text }}
        />
      ) : (
        <StyledBasicParagraph role="alert" $color={textColor}>
          {text}
        </StyledBasicParagraph>
      )}
    </Wrapper>
  );
};

Toast.Type = Type;

export default Toast;
